import type { AgeBand } from '../types/prisma-enums'
import type { GameId } from '../curriculum'
import type { Reward } from '../pacing'

/**
 * What the child's own screens read — coins, stickers, levels, streak.
 *
 * 🔴 WHY THIS IS LOCAL AND NOT A FETCH, WHICH IS THE DECISION WORTH READING.
 *
 * Everything a round produces is already written: `shared/offline/writes`
 * enqueues events and progress, the outbox flushes them, and the server is the
 * record. What does not exist is a **read** — there is no device-context
 * endpoint that hands a `Player` back their own coins and stickers, and
 * `shared/api/` has no module for one because nothing has needed it.
 *
 * The alternative shapes are both worse:
 *
 *   · **Fetch it** — invents an endpoint, and puts the child's home screen on
 *     the network. offline-sync.md §1 is explicit that *"gameplay never awaits
 *     the network"*, and B1 is the first screen after the tap.
 *   · **Show nothing** — a home screen with 0 coins and an empty sticker book
 *     after a child has played all week. design-system.md §12: *"a zero never
 *     contradicts the panel beside it"*, and this zero would contradict
 *     everything the child remembers doing.
 *
 * So this is a local mirror of writes we have already made. It is not the
 * truth; the server is. It is what the child sees while offline, and it is
 * exactly as correct as the queue that is carrying the same facts upstream.
 *
 * ⚠️ WHICH MEANS IT DIVERGES ON A SHARED DEVICE, and that is a real limit
 * rather than a bug to paper over: two children on one laptop share this
 * record. The product's answer is the teacher's Play Mode handoff (D2/D3) —
 * a roster tap that names the child — and that is a different entry point from
 * `#/play`. 📋 Reported rather than silently averaged.
 */

const KEY = 'kalqy.progress'

/** Collected once by an adult, and PERMANENT — see `ensureDevice` and OD-40. */
export interface ChildProfile {
  name: string
  ageBand: AgeBand
}

export interface KidProgress {
  /**
   * 🔴 WHOSE MIRROR THIS IS, AND ITS ABSENCE WAS THE SHARED-TABLET BUG.
   *
   * This record used to belong to the DEVICE, and the docblock above called that
   * a real limit whose answer was Teacher Play Mode — a feature that is disabled.
   * Once a child names themselves at sign-in there is a better answer available
   * for free: remember which child the mirror is for, and start a new one when
   * somebody else signs in.
   *
   * Without it, child B signing in on the classroom laptop after child A saw A's
   * coins, A's stickers and A's unlocked tiles on their own home screen — and
   * every one of those is a reward, which `pacing.md` treats as the thing a
   * four-year-old is actually playing for.
   *
   * ⚠️ IT IS NOT NAMESPACING, AND THE DIFFERENCE IS HONEST. Progress is still ONE
   * record; a child who hands the tablet on and comes back later starts empty
   * locally. The server holds the durable copy — this is a mirror (see the header)
   * — so what is lost is today's local view, not the child's history. Keeping N
   * children's mirrors on a shared tablet is the namespacing `kidSession.ts`
   * reports as not done, and it is a storage-budget decision rather than this one.
   */
  playerId: string | null
  profile: ChildProfile | null
  coins: number
  /** Sticker ids, `${gameId}-l${level}`. A set in spirit; an array on disk. */
  stickers: string[]
  /** Highest level completed per game, 0–5. Drives every tile. */
  completed: Partial<Record<GameId, number>>
  streakDays: number
  /** `YYYY-MM-DD` of the last day a round was finished. */
  lastPlayed: string | null
}

export function emptyProgress(): KidProgress {
  return {
    playerId: null,
    profile: null,
    coins: 0,
    stickers: [],
    completed: {},
    streakDays: 0,
    lastPlayed: null,
  }
}

/**
 * Minimal storage shape, injectable so tests and Node need no DOM — and so a
 * browser that throws on access cannot take the child's boot path down. The
 * same shim `device.ts` and `parentSession.ts` use, deliberately.
 */
export interface ProgressStorage {
  getItem(key: string): string | null
  setItem(key: string, value: string): void
}

function defaultStorage(): ProgressStorage | null {
  try {
    return (globalThis as { localStorage?: ProgressStorage }).localStorage ?? null
  } catch {
    return null
  }
}

let storage: ProgressStorage | null = defaultStorage()

/** Test seam, and the escape hatch if a browser's storage throws. */
export function setProgressStorage(next: ProgressStorage | null): void {
  storage = next
}

export function readProgress(): KidProgress {
  // Safari private mode throws on ACCESS rather than on use, so the read is
  // wrapped rather than truthiness-checked — the same shape as `device.ts`.
  const raw = ((): string | null => {
    try {
      return storage?.getItem(KEY) ?? null
    } catch {
      return null
    }
  })()
  if (!raw) return emptyProgress()

  try {
    const parsed: unknown = JSON.parse(raw)
    if (typeof parsed !== 'object' || parsed === null) return emptyProgress()
    const p = parsed as Partial<KidProgress>

    /*
     * 🔴 EVERY FIELD IS RE-VALIDATED, NOT CAST. This string survives app
     * versions, and a shape from an older build that reached `HomeScreen` as
     * `coins: undefined` would render "NaN" to a four-year-old. Falling back per
     * field means a partially-unrecognised record still yields a usable screen.
     */
    return {
      /* An older record has no `playerId`; `null` reads as "belongs to nobody",
         so the first child to sign in claims a fresh mirror rather than
         inheriting whatever the device-child left behind. */
      playerId: typeof p.playerId === 'string' && p.playerId.length > 0 ? p.playerId : null,
      profile: readProfile(p.profile),
      coins: typeof p.coins === 'number' && Number.isFinite(p.coins) ? Math.max(0, p.coins) : 0,
      stickers: Array.isArray(p.stickers) ? p.stickers.filter((s) => typeof s === 'string') : [],
      completed: readCompleted(p.completed),
      streakDays:
        typeof p.streakDays === 'number' && Number.isFinite(p.streakDays)
          ? Math.max(0, Math.trunc(p.streakDays))
          : 0,
      lastPlayed: typeof p.lastPlayed === 'string' ? p.lastPlayed : null,
    }
  } catch {
    return emptyProgress()
  }
}

function readProfile(value: unknown): ChildProfile | null {
  if (typeof value !== 'object' || value === null) return null
  const { name, ageBand } = value as Partial<ChildProfile>
  if (typeof name !== 'string' || name.trim().length === 0) return null
  if (ageBand !== 'BAND_3_4' && ageBand !== 'BAND_4_5' && ageBand !== 'BAND_5_6') return null
  return { name, ageBand }
}

function readCompleted(value: unknown): Partial<Record<GameId, number>> {
  if (typeof value !== 'object' || value === null) return {}
  const out: Partial<Record<GameId, number>> = {}
  for (const [k, v] of Object.entries(value as Record<string, unknown>)) {
    // Clamped to the five-level model: a stored 9 would light every tile.
    if (typeof v === 'number' && Number.isFinite(v)) {
      out[k as GameId] = Math.min(5, Math.max(0, Math.trunc(v)))
    }
  }
  return out
}

function write(next: KidProgress): KidProgress {
  try {
    storage?.setItem(KEY, JSON.stringify(next))
  } catch {
    /*
     * Non-fatal, like `device.ts`. A device that cannot persist still plays —
     * it loses the mirror, not the session, and the queue carrying the same
     * facts to the server is untouched. Throwing here would put a storage quirk
     * in front of a four-year-old.
     */
  }
  return next
}

/**
 * A child has just signed in. Hand back the mirror that belongs to them.
 *
 * 🔴 THE SAME CHILD KEEPS THEIRS; ANYBODY ELSE STARTS EMPTY. This replaced
 * `setChildProfile`, which merged a new name into whatever was already on disk
 * — `{ ...readProgress(), profile }` — so on a classroom laptop the second child
 * of the morning saw the first child's coins, stickers and unlocked tiles under
 * their own name. Nothing was wrong with that while the tablet WAS the child;
 * the sign-in is what made it wrong, and it is also what makes it fixable.
 *
 * ⚠️ THE NAME IS STILL REFRESHED FOR A RETURNING CHILD, because the roster is
 * the authority on it. A correction a teacher makes — a spelling, a surname
 * added — reaches the tablet on the next sign-in instead of being pinned by the
 * first one, which is the failure `SetupScreen` had by construction (OD-40: *"the
 * first name sent is permanent"*).
 */
/**
 * The durable copy, as `GET /progress/:playerId` answers with it.
 *
 * ⚠️ `lastPlayed` IS AN ISO INSTANT HERE AND A `YYYY-MM-DD` DAY KEY IN THE
 * MIRROR. The server stores when the child played; the streak rule only ever
 * asks which DAY that was, in the reader's own zone. Converting on the way in is
 * what keeps `nextStreak` comparing like with like.
 */
export interface DurableProgress {
  coins: number
  stickers: string[]
  streakDays: number
  lastPlayed: string | null
  /**
   * Highest level PASSED per game, as the server holds it (ADR-058). Optional:
   * an older API does not send it.
   */
  levels?: Partial<Record<string, number>>
}

function dayKeyOf(iso: string | null): string | null {
  if (iso === null) return null
  const at = new Date(iso)
  return Number.isNaN(at.getTime()) ? null : dayKey(at)
}

export function beginChild(playerId: string, profile: ChildProfile): KidProgress {
  const current = readProgress()
  if (current.playerId === playerId) return write({ ...current, profile })
  return write({ ...emptyProgress(), playerId, profile })
}

/**
 * Fill a just-claimed mirror from the server's durable copy.
 *
 * 🔴 THIS IS WHAT STOPS A SHARED LAPTOP DELETING A CHILD'S TERM. The header
 * above says an empty local mirror costs *"today's local view, not the child's
 * history"* — true only while nothing wrote the mirror back. `POST
 * /progress/:playerId` upserts a SNAPSHOT and never increments, so the first
 * level a freshly-begun child finished sent `coins: 6` as the whole truth about
 * them, and forty-seven coins, two stickers and a three-day streak stopped
 * existing the moment the teacher handed the tablet to the next child. Seeding
 * makes the outgoing snapshot a continuation instead of a replacement.
 *
 * 🔴 IT REFUSES TO OVERWRITE A MIRROR THAT HAS ALREADY EARNED SOMETHING. Both
 * callers begin the child, then fetch; a round finishing in between is rare and
 * entirely possible, and its coins are newer than anything the server can be
 * holding. An untouched mirror is the only one it is safe to replace, and that
 * is a fact this function checks rather than a sequence the callers promise.
 *
 * ⚠️ AND A DIFFERENT CHILD NOW OWNING THE MIRROR IS A NO-OP, not an overwrite.
 * A slow answer for the child who has just handed the tablet on must never land
 * on the one holding it — the failure this whole module exists to prevent.
 *
 * ⚠️ `completed` IS NOT SEEDED, because the durable copy does not carry it. The
 * unlock tiles rebuild from play; the coins a child owns cannot.
 */
export function seedFromDurable(playerId: string, durable: DurableProgress): KidProgress {
  const fetched = readProgress()
  if (fetched.playerId !== playerId) return fetched

  /*
   * 🔴 LEVELS ALWAYS MERGE, BY MAXIMUM — EVEN INTO A MIRROR THAT HAS EARNED
   * SOMETHING. They are the one part of progress that must follow the child
   * from laptop to laptop (ADR-058): a child who passed level 2 on Monday's
   * laptop has level 3 open on Tuesday's. Max is safe in any order — a passed
   * level is never taken back, whichever side saw it first.
   */
  const levels = durable.levels ?? {}
  const merged = { ...fetched.completed }
  let raised = false
  for (const [game, level] of Object.entries(levels)) {
    if (typeof level !== 'number' || !Number.isInteger(level)) continue
    const have = merged[game as GameId] ?? 0
    if (level > have) {
      merged[game as GameId] = level
      raised = true
    }
  }
  const current = raised ? write({ ...fetched, completed: merged }) : fetched

  const untouched =
    current.coins === 0 &&
    current.stickers.length === 0 &&
    current.streakDays === 0 &&
    current.lastPlayed === null
  if (!untouched) return current

  return write({
    ...current,
    coins: Number.isFinite(durable.coins) ? Math.max(0, Math.trunc(durable.coins)) : 0,
    stickers: [...new Set(durable.stickers.filter((id) => typeof id === 'string'))],
    streakDays: Number.isFinite(durable.streakDays)
      ? Math.max(0, Math.trunc(durable.streakDays))
      : 0,
    lastPlayed: dayKeyOf(durable.lastPlayed),
  })
}

/** `YYYY-MM-DD` in LOCAL time — a school day, not a UTC one. */
export function dayKey(at: Date): string {
  const y = at.getFullYear()
  const m = String(at.getMonth() + 1).padStart(2, '0')
  const d = String(at.getDate()).padStart(2, '0')
  return `${String(y)}-${m}-${d}`
}

/**
 * 🔴 WHAT "PASSED" MEANS FOR A LEVEL — two stars, i.e. half the round right
 * (`calculateReward`). Only a passed round opens the next level (ADR-058); a
 * one-star round still pays its coins and sticker, and the level stays open to
 * play again — "not yet", never "failed".
 */
export const LEVEL_PASS_STARS = 2

/**
 * A finished round, folded in.
 *
 * ⚠️ `completed` TAKES THE MAXIMUM, NEVER THE LATEST. A child replaying level 2
 * after clearing level 4 has not un-cleared 4, and a tile that dimmed because
 * they went back to an easier level is the kind of thing a four-year-old reads
 * as punishment for choosing it.
 */
export function recordRound(
  gameId: GameId,
  level: number,
  reward: Reward,
  now: Date = new Date(),
): KidProgress {
  const prev = readProgress()
  const today = dayKey(now)

  const stickers = reward.sticker
    ? [...new Set([...prev.stickers, `${gameId}-l${String(level)}`])]
    : prev.stickers

  return write({
    ...prev,
    coins: prev.coins + reward.coins,
    stickers,
    completed:
      reward.stars >= LEVEL_PASS_STARS
        ? { ...prev.completed, [gameId]: Math.max(prev.completed[gameId] ?? 0, level) }
        : prev.completed,
    streakDays: nextStreak(prev, today, now),
    lastPlayed: today,
  })
}

/**
 * 🔴 A STREAK BREAKS ON A MISSED DAY, AND IT MUST NOT BREAK ON A SECOND ROUND.
 *
 * Playing twice in one afternoon is the common case, and incrementing per round
 * would show a four-year-old "12 days in a row" on their first afternoon —
 * which makes the number meaningless the first time an adult looks at it.
 */
function nextStreak(prev: KidProgress, today: string, now: Date): number {
  if (prev.lastPlayed === today) return Math.max(1, prev.streakDays)

  const yesterday = dayKey(new Date(now.getTime() - 86_400_000))
  if (prev.lastPlayed === yesterday) return prev.streakDays + 1

  // A gap, a first ever round, or a clock that moved backwards: today is day 1.
  return 1
}
